import axiosClient from '../../api/axiosClient.js';
import { fetchPageData } from '../../api/utils.js';
import AuthService from '../../auth/AuthService';

async function getGeneralInfo() {
  const user = AuthService.getUser();
  const url = user?.is_instructor ? `/api/general-info/` : `/api/student/general-info/`;
  const { data } = await axiosClient.get(url);
  return data;
}

export const generalInfoQuery = () => {
  return {
    queryKey: ['generalInfo'],
    queryFn: async () => getGeneralInfo(),
    refetchOnWindowFocus: false,
    staleTime: Infinity
  };
};

// Trimmed general-info payload for the summary pages. Backend early-
// exits when any of the `?principles / ?classification / ?normal /
// ?balancedequation` params is present, returning only `games`
// (id / game_type / name / scenario) and `sessions` (id / user / game
// / completed / attempt) filtered to the current user + selected game
// type(s). Distinct queryKey so it doesn't clash with the full
// generalInfoQuery cache used by game-play pages.
async function getSummaryGeneralInfo() {
  const user = AuthService.getUser();
  const base = user?.is_instructor ? `/api/general-info/` : `/api/student/general-info/`;
  const { data } = await axiosClient.get(`${base}?principles`);
  return data;
}

export const generalInfoSummaryQuery = () => {
  return {
    queryKey: ['generalInfo', 'summary', 'principles'],
    queryFn: async () => getSummaryGeneralInfo(),
    refetchOnWindowFocus: false,
    staleTime: Infinity
  };
};

async function getConsolidatedScores() {
  const { data } = await axiosClient.get(`/api/scores/`);
  return data;
}

export const consolidatedScoresQuery = () => {
  return {
    queryKey: ['consolidatedScores'],
    queryFn: async () => getConsolidatedScores(),
    refetchOnWindowFocus: false
  };
};

// Trimmed consolidated-scores payload for the summary pages. Always
// scopes to the matching game type (`?principles` here) so the response
// only carries the bucket the page reads. When the caller is an
// instructor, appends `&user=<their_id>` so the endpoint returns just
// the instructor's own scores instead of all students' scores across
// the course. Distinct queryKey (includes the instructor id when
// applicable) so this cache stays separate from consolidatedScoresQuery
// and doesn't cross-pollinate if the same user later switches accounts.
async function getSummaryConsolidatedScores() {
  const user = AuthService.getUser();
  const params = ['principles'];
  if (user?.is_instructor && user?.id != null) {
    params.push(`user=${user.id}`);
  }
  const { data } = await axiosClient.get(`/api/scores/?${params.join('&')}`);
  return data;
}

export const consolidatedScoresSummaryQuery = () => {
  const user = AuthService.getUser();
  const scopedUserId = user?.is_instructor ? (user?.id ?? null) : null;
  return {
    queryKey: ['consolidatedScores', 'summary', 'principles', scopedUserId],
    queryFn: async () => getSummaryConsolidatedScores(),
    refetchOnWindowFocus: false
  };
};

async function getConsolidatedInsights({userId} = {}) {
  const url = userId ? `/api/insights/?user=${userId}` : `/api/insights/`;
  const { data } = await axiosClient.get(url);
  return data;
}

export const consolidatedInsightsQuery = ({userId} = {}) => {
  return {
    queryKey: ['consolidatedInsights', userId ?? null],
    queryFn: async () => getConsolidatedInsights({userId}),
    refetchOnWindowFocus: false
  };
};

async function getScenarioLevels({scenarioId}) {  
  return scenarioId ? fetchPageData(`/api/games/scenario-levels/?scenarios=${scenarioId}`) : [];
}

export const scenarioLevelsQuery = ({scenarioId}) => {
  return {
    queryKey: ['scenarioLevels', scenarioId],
    queryFn: async () => getScenarioLevels({scenarioId}),
    refetchOnWindowFocus: false,
    staleTime: Infinity
  };
};

export async function makeTransactionDecision({
  game,
  gameSessionId,
  question,
  selectedOption,
  timeElapsed,
  gameTimeElapsed,
  timestamp
}) {
  const { data } = await axiosClient.post(`api/principles/decisions/`, {
    game,
    game_session: gameSessionId,
    question,
    selected_option: selectedOption,
    time_elapsed: timeElapsed || 1,
    game_time_elapsed: gameTimeElapsed || 1,
    timestamp
  });

  return data;
}

export async function updateGameSession({id, levelNumber, status, isSessionCompleted}) {
  const levelKey = `level${levelNumber}`;
  const requestBody = {
    is_ready: true,
    [levelKey]: status,
  };

  if (isSessionCompleted) {
    requestBody.completed = true;
  }

  const { data } = await axiosClient.patch(`/api/games/sessions/${id}/`, requestBody);

  return data;
}

export async function getScoresStats({gameId}) {
  const response = await axiosClient.get(`/api/principles/scores/stats/${gameId}/`);
  return response.data;
}

export const scoresStatsQuery = ({gameId}) => {
  return {
    queryKey: ['scoresStats'],
    queryFn: async () => getScoresStats({gameId}),
    refetchOnWindowFocus: false
  };
};

export async function getScoresPrinciples({userId, gameId}) {
  const response = await axiosClient.get(`/api/principles/scores/principles/?user=${userId}&game=${gameId}`);
  return response.data;
}

export const scoresPrinciplesQuery = ({userId, gameId}) => {
  return {
    queryKey: ['scoresPrinciples'],
    queryFn: async () => getScoresPrinciples({userId, gameId}),
    refetchOnWindowFocus: false
  };
};


export async function getTransactionDecisions({gameSessionId}) {
  return fetchPageData(`/api/principles/decisions/?game_session=${gameSessionId}`);
}

export const decisionsQuery = ({gameSessionId}) => {
  return {
    queryKey: ['decisions', { gameSessionId }],
    queryFn: () => getTransactionDecisions({ gameSessionId }),
    refetchOnWindowFocus: false,
    enabled: !!gameSessionId
  };
};


export async function getPrinciplesQuestions({scenarioLevelId}) {
  return fetchPageData(`/api/principles/principles-questions/?scenario_level=${scenarioLevelId}`);
}

export const principlesQuestionsQuery = ({scenarioLevelId}) => {
  return {
    queryKey: ['principlesQuesions', scenarioLevelId],
    queryFn: async () => getPrinciplesQuestions({scenarioLevelId}),
    refetchOnWindowFocus: false,
    cacheTime: 0,
    enabled: !!scenarioLevelId
  };
};


export async function getScores({gameId, userId, gameSessionId}) {
  return fetchPageData(`/api/principles/scores/?game=${gameId}&user=${userId}&game_session=${gameSessionId}`);
}

export const scoresQuery = ({gameId, userId, gameSessionId}) => {
  return {
    queryKey: [`sessions_${gameSessionId}`],
    queryFn: async () => getScores({gameId, userId, gameSessionId}),
    refetchOnWindowFocus: false,
    enabled: !!gameId && !!userId && !!gameSessionId
  };
};

export async function getScoresInsights({gameId}) {
  const response = await axiosClient.get(`/api/principles/scores/principles/?game=${gameId}`);
  return response.data;
}

export const scoresInsightsQuery = ({gameId}) => {
  return {
    queryKey: [`scoresInsights`],
    queryFn: async () => getScoresInsights({gameId}),
    refetchOnWindowFocus: false,
    enabled: !!gameId
  };
};

export async function getScoresItemInsights({gameId}) {
  return fetchPageData(`/api/principles/scores/insights/?game=${gameId}`);
}

export const scoresItemInsightsQuery = ({gameId}) => {
  return {
    queryKey: ['scoresItemInsights', gameId],
    queryFn: async () => getScoresItemInsights({gameId}),
    refetchOnWindowFocus: false,
    enabled: !!gameId
  };
};

export async function getDecisionsInsights({gameId, gameSessionId}) {
  const response = await axiosClient.get(`/api/principles/decisions/insights/${gameId}/${gameSessionId}`);
  return response?.data;
}

export const decisionsInsigtsQuery = ({gameId, gameSessionId}) => {
  return {
    queryKey: [`principlesDecisionsInsights`],
    queryFn: async () => getDecisionsInsights({gameId, gameSessionId}),
    enabled: !!gameId && !!gameSessionId,
    refetchOnWindowFocus: false
  }
}

export async function updateFeedback({id, feedback1, feedback2, feedback3}) {
  const { data } = await axiosClient.patch(`/api/games/sessions/${id}/`, {
    feedback1,
    feedback2,
    feedback3
  });

  return data;
}


async function getTransactions() {
  return fetchPageData(`/api/games/transactions/`);
}

export const transactionsQuery = () => {
  return {
    queryKey: ['transactionsThePrinciples'],
    queryFn: async () => getTransactions(),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

export async function getGamesTransaction({id}) {
  const response = await axiosClient.get(`/api/games/transactions/${id}/`);
  return response;
}

export async function getGamesItem({ id }) {
  const response = await axiosClient.get(`/api/games/items/${id}/`);
  return response.data;
}

export const gamesItemQuery = ({ id }) => ({
  queryKey: ['gamesItem', id],
  queryFn: () => getGamesItem({ id }),
  enabled: !!id,
  staleTime: Infinity,
  refetchOnWindowFocus: false,
});

async function fetchGamesPage(url, allGames = []) {
  const response = await axiosClient.get(url);
  const games = [...allGames, ...response.data.results];

  if (response.data.next) {
    return fetchGamesPage(response.data.next, games);
  } else {
    return games;
  }
}

async function getGames() {
  return fetchGamesPage(`/api/games/games/`);
}

export const gamesQuery = () => {
  return {
    queryKey: ['games'],
    queryFn: async () => getGames(),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

async function fetchGamesSessions(url, allSessions = []) {
  const response = await axiosClient.get(url);
  const sessions = [...allSessions, ...response.data.results];

  if (response.data.next) {
    return fetchGamesSessions(response.data.next, sessions);
  } else {
    return sessions;
  }
}

export async function getGamesSessions() {
  return fetchGamesSessions(`/api/games/sessions/`);
}

async function fetchItemsPage(url, allItems = []) {
  const response = await axiosClient.get(url);
  const items = [...allItems, ...response.data.results];

  if (response.data.next) {
    return fetchItemsPage(response.data.next, items);
  } else {
    return items;
  }
}

async function getItems() {
  return fetchItemsPage(`/api/games/items/`);
}

export const itemsQuery = () => {
  return {
    queryKey: ['items'],
    queryFn: async () => getItems(),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

async function getScenarioLevelsView(scenarioId) {
  const response = await axiosClient.get(`/api/games/scenario-levels/view/?scenarios=${scenarioId}`);
  return response.data;
}

export const scenarioLevelsViewQuery = (scenarioId) => {
  return {
    queryKey: ['scenarioLevelsView', scenarioId],
    queryFn: async () => getScenarioLevelsView(scenarioId),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: !!scenarioId
  };
};

export async function postDecisionsBulk(decisions) {
  const { data } = await axiosClient.post(`api/principles/decisions/bulk/`, decisions);
  return data;
}